import { useState, useCallback, useMemo } from 'react';
import { T, amountStyle } from '../theme';
import { loadFile, saveFile, saveTransactionAccount } from '../utils/firestoreStorage';
import { computeAccountBalance } from '../utils/accountBalance';
import {
  BIZ_CLASS, CLASS_SELECT_KEYS, CATEGORIES, INCOME_CATEGORIES,
  CATEGORY_KEY_BY_LABEL, INCOME_CAT_KEY_BY_LABEL, CATEGORY_KEYS,
} from '../utils/constants';
import { categoryLabel, normalizeCategory, makeKey } from '../utils/categoryHelpers';
import { FinanceViewTopBar } from './NavShared';
import AddTransactionModal from './AddTransactionModal';
import EditModal from './EditModal';
import SortFilterBar, { labelStyle, inputStyle } from './SortFilterBar';
import { getPresetRange } from './DateRangePicker';
import { formatAmount } from '../utils/formatters';
import {
  ChevronDown, Plus, Upload, Edit2, Trash2, Link2, ArrowLeft, Unlink,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const INCOME_CLS  = new Set(['business_income', 'unclassified_credit', 'personal_income']);
const EXPENSE_CLS = new Set(['business_expense', 'unclassified_debit', 'personal_expense',
                              'business_expense_refund', 'personal_expense_refund']);

const SORT_OPTIONS = [
  { value: 'date',   label: 'Date',   defaultDir: 'desc' },
  { value: 'amount', label: 'Amount', defaultDir: 'desc' },
  { value: 'vendor', label: 'Vendor', defaultDir: 'asc'  },
];

const GROUP_SECTIONS = [{ options: [
  { value: null,             label: 'None'           },
  { value: 'month',          label: 'Month'          },
  { value: 'classification', label: 'Classification' },
  { value: 'category',       label: 'Category'       },
]}];

const EMPTY_FILTERS = {
  vendor: '', classification: '', category: '', minAmount: '', maxAmount: '',
};

// ─── Small inline selects (reused from BSV) ───────────────────────────────────

function ClassSelect({ value, onChange }) {
  const effective = value === 'unclassified' ? 'unclassified_debit' : (value || 'unclassified_debit');
  return (
    <select
      value={effective}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{ fontSize: 11, padding: '3px 6px', borderRadius: 6, border: '1.5px solid #D1D5DB', cursor: 'pointer', background: '#fff', fontWeight: 600 }}
    >
      {CLASS_SELECT_KEYS.map(k => <option key={k} value={k}>{BIZ_CLASS[k].label}</option>)}
    </select>
  );
}

function CategorySelect({ value, onChange, categories, isIncome }) {
  const activeColor = isIncome ? '#15803d' : T.tealDeep;
  const activeBg   = isIncome ? '#f0fdf4' : T.tealPale;
  const norm       = normalizeCategory(value) || '';
  return (
    <select
      value={norm}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      style={{
        fontSize: 11, padding: '3px 6px', borderRadius: 6, cursor: 'pointer', fontWeight: 600,
        border: `1.5px solid ${value ? activeColor : '#D1D5DB'}`,
        background: value ? activeBg : '#fff', color: value ? activeColor : '#374151',
      }}
    >
      <option value="">— category —</option>
      {categories.map(c => {
        const key = CATEGORY_KEY_BY_LABEL[c] ?? INCOME_CAT_KEY_BY_LABEL[c] ?? makeKey(c);
        return <option key={key} value={key}>{c}</option>;
      })}
    </select>
  );
}

// ─── Group/sort helpers ───────────────────────────────────────────────────────

function getGroupKey(tx, groupBy) {
  switch (groupBy) {
    case 'month':          return tx.date ? tx.date.slice(0, 7) : 'No Date';
    case 'classification': return tx.businessClassification || (tx.transactionDirection === 'credit' ? 'unclassified_credit' : 'unclassified_debit');
    case 'category':       return normalizeCategory(tx.category) || 'uncategorized';
    default:               return '';
  }
}

function formatGroupLabel(key, groupBy) {
  if (groupBy === 'month') {
    if (!key || key === 'No Date') return key;
    const [y, m] = key.split('-');
    return new Date(+y, +m - 1).toLocaleString(undefined, { month: 'long', year: 'numeric' });
  }
  if (groupBy === 'classification') return BIZ_CLASS[key]?.label ?? key;
  if (groupBy === 'category') return key === 'uncategorized' ? 'Uncategorized' : (categoryLabel(key) || key);
  return key;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TransactionAccountView({
  uid,
  savedFiles,
  filename,
  allLoaded,
  setAllLoaded,
  defaultCurrency,
  categories = CATEGORIES,
  incomeCategories = INCOME_CATEGORIES,
  addCategory,
  addIncomeCategory,
  onGoToDashboard,
  onImportCreditCard,
  onImportBank,
  onImportBrokerage,
  topBarProps,
  transactionAccounts = [],
  onTransactionAccountsChange,
  onFilePatch,
}) {
  const fileInfo   = savedFiles.find(f => f.name === filename);
  const accountName = fileInfo?.accountName ?? filename;
  const reportType  = fileInfo?.reportType ?? 'credit_card';

  // All files belonging to this account (may span multiple import batches)
  const accountFiles = useMemo(
    () => savedFiles.filter(f => (f.accountName || f.name) === accountName && (f.reportType === 'credit_card' || f.reportType === 'bank' || f.reportType === 'brokerage')),
    [savedFiles, accountName]
  );

  // Merge all account files; tag each tx with its source file for targeted saves
  const rawData = useMemo(
    () => accountFiles.flatMap(f => (allLoaded[f.name] ?? []).map(tx => ({ ...tx, _sourceFile: f.name }))),
    [accountFiles, allLoaded]
  );

  // ── Sort / filter / group state ──
  const [sortBy,  setSortBy]  = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [groupBy, setGroupBy] = useState(null);
  const [dateFrom, setDateFrom] = useState(() => getPresetRange('this_month').from);
  const [dateTo,   setDateTo]   = useState(() => getPresetRange('this_month').to);

  const setFilter          = (k, v) => setFilters(prev => ({ ...prev, [k]: v }));
  const clearFilters       = () => setFilters(EMPTY_FILTERS);
  const activeFilterCount  = Object.values(filters).filter(Boolean).length;

  // ── CRUD state ──
  const [showAddModal,  setShowAddModal]  = useState(false);
  const [expandedId,    setExpandedId]    = useState(null);
  const [editingTx,     setEditingTx]     = useState(null);
  const [saving,        setSaving]        = useState(false);
  const [unlinkingId,   setUnlinkingId]   = useState(null);

  // ── Account settings edit state ──
  const [showObEdit, setShowObEdit]   = useState(false);
  const [obAmount,   setObAmount]     = useState('');
  const [obDate,     setObDate]       = useState('');
  const [obType,     setObType]       = useState('personal');
  const [obSaving,   setObSaving]     = useState(false);
  const [obError,    setObError]      = useState('');

  // ── Balance computation ──
  const accountMeta  = transactionAccounts.find(a => (a.name || a.id) === accountName) ?? {};
  const allTxsForAccount = useMemo(
    () => accountFiles.flatMap(f => allLoaded[f.name] ?? []),
    [accountFiles, allLoaded]
  );
  const balanceResult = useMemo(() => computeAccountBalance({
    transactions: allTxsForAccount,
    openingBalance: accountMeta.openingBalance,
    openingBalanceDate: accountMeta.openingBalanceDate,
    reportType,
    asOfDate: null,
  }), [allTxsForAccount, accountMeta, reportType]);

  const handleSaveObEdit = async () => {
    const parsed = obAmount.trim() !== '' ? parseFloat(obAmount) : undefined;
    if (obAmount.trim() !== '' && (parsed === undefined || isNaN(parsed))) { setObError('Enter a valid number.'); return; }
    setObSaving(true);
    setObError('');
    try {
      await saveTransactionAccount(uid, {
        name: accountName,
        reportType,
        type: obType,
        openingBalance: parsed,
        openingBalanceDate: obDate || undefined,
      });
      await onTransactionAccountsChange?.();
      setShowObEdit(false);
    } catch (e) {
      setObError('Failed to save. Please try again.');
    }
    setObSaving(false);
  };

  // ── Filtered + sorted transactions ──
  const filtered = useMemo(() => {
    let result = rawData.filter(tx => {
      if (filters.vendor) {
        if (!(tx.vendor || '').toLowerCase().includes(filters.vendor.toLowerCase())) return false;
      }
      if (filters.classification && tx.businessClassification !== filters.classification) return false;
      if (filters.category) {
        if (normalizeCategory(tx.category) !== filters.category) return false;
      }
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (filters.minAmount !== '' && amt < parseFloat(filters.minAmount)) return false;
      if (filters.maxAmount !== '' && amt > parseFloat(filters.maxAmount)) return false;
      if (dateFrom && (tx.date ?? '') < dateFrom) return false;
      if (dateTo   && (tx.date ?? '') > dateTo)   return false;
      return true;
    });

    result.sort((a, b) => {
      let aVal, bVal;
      if (sortBy === 'amount') {
        aVal = parseFloat(a.convertedTotal ?? a.total ?? a.amount ?? 0);
        bVal = parseFloat(b.convertedTotal ?? b.total ?? b.amount ?? 0);
      } else if (sortBy === 'vendor') {
        aVal = (a.vendor || '').toLowerCase();
        bVal = (b.vendor || '').toLowerCase();
      } else {
        aVal = a.date ?? '';
        bVal = b.date ?? '';
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ?  1 : -1;
      return 0;
    });

    return result;
  }, [rawData, filters, dateFrom, dateTo, sortBy, sortDir]);

  // ── Filtered totals ──
  const filteredTotals = useMemo(() => {
    let debits = 0, credits = 0;
    for (const tx of filtered) {
      const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
      if (tx.transactionDirection === 'credit') credits += amt;
      else debits += amt;
    }
    return { debits, credits };
  }, [filtered]);

  // ── Grouped ──
  const grouped = useMemo(() => {
    if (!groupBy) return null;
    const map = new Map();
    for (const tx of filtered) {
      const key = getGroupKey(tx, groupBy);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(tx);
    }
    const entries = [...map.entries()];
    if (groupBy === 'month') {
      entries.sort((a, b) => b[0].localeCompare(a[0]));
    } else {
      entries.sort((a, b) => a[0].localeCompare(b[0]));
    }
    return entries.map(([key, txs]) => {
      let debits = 0, credits = 0;
      for (const tx of txs) {
        const amt = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
        if (tx.transactionDirection === 'credit') credits += amt;
        else debits += amt;
      }
      return { key, label: formatGroupLabel(key, groupBy), txs, debits, credits };
    });
  }, [filtered, groupBy]);

  // ── Save helper — routes to the correct source file ──
  const saveData = useCallback(async (targetFilename, newData) => {
    if (!uid) return;
    const tfi = savedFiles.find(f => f.name === targetFilename);
    if (tfi?.local) return;
    setSaving(true);
    try {
      await saveFile(uid, targetFilename, newData, { imageStorage: false, reportType: tfi?.reportType ?? reportType, accountName: tfi?.accountName });
      setAllLoaded(prev => ({ ...prev, [targetFilename]: newData }));
    } finally {
      setSaving(false);
    }
  }, [uid, savedFiles, reportType, setAllLoaded]);

  const handleClassChange = useCallback(async (id, v) => {
    const tx = rawData.find(t => t.id === id);
    if (!tx) return;
    const src = tx._sourceFile;
    await saveData(src, (allLoaded[src] ?? []).map(t => t.id === id ? { ...t, businessClassification: v } : t));
  }, [rawData, allLoaded, saveData]);

  const handleCategoryChange = useCallback(async (id, v) => {
    const tx = rawData.find(t => t.id === id);
    if (!tx) return;
    const src = tx._sourceFile;
    await saveData(src, (allLoaded[src] ?? []).map(t => t.id === id ? { ...t, category: v || undefined } : t));
  }, [rawData, allLoaded, saveData]);

  const openEdit = useCallback((tx) => {
    const paired = (tx.pairedWithId && tx.pairedWithFile)
      ? allLoaded[tx.pairedWithFile]?.find(r => r.id === tx.pairedWithId) ?? null
      : null;
    const mergedNotes = paired?.notes && paired.notes !== tx.notes
      ? [tx.notes, paired.notes].filter(Boolean).join('\n')
      : tx.notes;
    const src = tx._sourceFile ?? filename;
    const srcFi = savedFiles.find(f => f.name === src);
    const merged = {
      ...tx,
      ...(paired ? {
        businessClassification: tx.businessClassification ?? paired.businessClassification,
        category: paired.category ?? tx.category,
        notes: mergedNotes,
        ...(!tx.imagePreview && paired.imagePreview ? { imagePreview: paired.imagePreview, mimeType: paired.mimeType, imageId: paired.imageId } : {}),
      } : {}),
      _reportName: src,
      _reportType: srcFi?.reportType ?? reportType,
    };
    const isManual = tx.sourceFormat === 'Manual Entry';
    const locked = isManual ? [] : ['vendor', 'total', 'subtotal', 'tax', 'tip', 'date', 'currency'];
    setEditingTx({ ...merged, _lockedFields: locked });
  }, [allLoaded, filename, reportType, savedFiles]);

  const handleEditSave = useCallback(async (updated) => {
    if (!editingTx || !uid) return;
    const { _reportName, _reportType, _sourceReports, _isDuplicate, _isLinked, _lockedFields, _sourceFile, ...clean } = updated;
    const target = _reportName ?? filename;
    const fileData = allLoaded[target];
    if (fileData) {
      const newData = fileData.map(r => r.id === clean.id ? clean : r);
      const fi = savedFiles.find(f => f.name === target);
      await saveFile(uid, target, newData, { imageStorage: false, reportType: fi?.reportType ?? _reportType ?? reportType, accountName: fi?.accountName });
      setAllLoaded(prev => ({ ...prev, [target]: newData }));
      onFilePatch?.(target, newData);
    }
    // Sync reimbursed flag to the paired item (linked expense↔transaction pairs)
    if (clean.pairedWithId && clean.pairedWithFile) {
      const pfi = savedFiles.find(f => f.name === clean.pairedWithFile);
      let pairedData = allLoaded[clean.pairedWithFile];
      if (!pairedData) {
        const loaded = await loadFile(uid, clean.pairedWithFile, pfi?.reportType ?? 'expense');
        pairedData = loaded?.data;
      }
      if (pairedData) {
        const newPairedData = pairedData.map(r =>
          String(r.id) === String(clean.pairedWithId)
            ? { ...r, reimbursed: clean.reimbursed || undefined }
            : r
        );
        await saveFile(uid, clean.pairedWithFile, newPairedData, { imageStorage: false, reportType: pfi?.reportType ?? 'expense', accountName: pfi?.accountName });
        setAllLoaded(prev => ({ ...prev, [clean.pairedWithFile]: newPairedData }));
        onFilePatch?.(clean.pairedWithFile, newPairedData);
      }
    }
    setEditingTx(null);
  }, [editingTx, uid, allLoaded, savedFiles, filename, reportType, setAllLoaded, onFilePatch]);

  const handleDelete = useCallback(async (tx) => {
    if (tx.pairedWithId) return;
    if (!window.confirm(`Delete "${tx.vendor}"? This cannot be undone.`)) return;
    const src = tx._sourceFile ?? filename;
    await saveData(src, (allLoaded[src] ?? []).filter(t => t.id !== tx.id));
  }, [allLoaded, filename, saveData]);

  const handleUnlink = useCallback(async (tx) => {
    if (!tx.pairedWithId || !tx.pairedWithFile || !uid) return;
    setUnlinkingId(tx.id);
    try {
      const src = tx._sourceFile ?? filename;
      const srcFi = savedFiles.find(f => f.name === src);
      const newData = (allLoaded[src] ?? []).map(t => t.id === tx.id ? { ...t, pairedWithId: undefined, pairedWithFile: undefined } : t);
      await saveFile(uid, src, newData, { imageStorage: false, reportType: srcFi?.reportType ?? reportType, accountName: srcFi?.accountName });
      setAllLoaded(prev => ({ ...prev, [src]: newData }));
      const pfi = savedFiles.find(f => f.name === tx.pairedWithFile);
      if (pfi) {
        const pf = await loadFile(uid, tx.pairedWithFile, pfi.reportType);
        if (pf?.data) {
          const pd = pf.data.map(r => r.id === tx.pairedWithId ? { ...r, pairedWithId: undefined, pairedWithFile: undefined } : r);
          await saveFile(uid, tx.pairedWithFile, pd, { imageStorage: false, reportType: pfi.reportType, accountName: pfi.accountName });
          setAllLoaded(prev => ({ ...prev, [tx.pairedWithFile]: pd }));
        }
      }
    } finally {
      setUnlinkingId(null);
    }
  }, [allLoaded, uid, filename, reportType, savedFiles, setAllLoaded]);

  const handleAddTransaction = useCallback(async (targetFile, txData) => {
    const newId = typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `manual_${Date.now()}`;
    const tfi    = savedFiles.find(f => f.name === targetFile);
    const existing = allLoaded[targetFile] ?? [];
    const newData  = [{ ...txData, id: newId }, ...existing];
    await saveFile(uid, targetFile, newData, { imageStorage: false, reportType: tfi?.reportType ?? reportType, accountName: tfi?.accountName });
    setAllLoaded(prev => ({ ...prev, [targetFile]: newData }));
    setShowAddModal(false);
  }, [uid, allLoaded, savedFiles, reportType, setAllLoaded]);

  const txFiles = useMemo(() => savedFiles.filter(f => f.reportType === 'credit_card' || f.reportType === 'bank' || f.reportType === 'brokerage'), [savedFiles]);

  const currency = defaultCurrency || 'USD';

  // ── Row renderer ──
  const renderTx = (tx) => {
    const isExpanded  = expandedId === tx.id;
    const isCredit    = tx.transactionDirection === 'credit';
    const amount      = parseFloat(tx.convertedTotal ?? tx.total ?? tx.amount ?? 0);
    const isPaired    = !!(tx.pairedWithId && tx.pairedWithFile);
    const cls         = tx.businessClassification || (isCredit ? 'unclassified_credit' : 'unclassified_debit');
    const clsConfig   = BIZ_CLASS[cls] || BIZ_CLASS.unclassified_debit;
    const showCatSelect = INCOME_CLS.has(cls) ? incomeCategories : EXPENSE_CLS.has(cls) ? categories : null;
    const isUnlinking = unlinkingId === tx.id;

    return (
      <div key={tx.id} style={{ background: clsConfig.bg, borderBottom: '1px solid #E5E7EB' }}>
        <div
          onClick={() => setExpandedId(isExpanded ? null : tx.id)}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', cursor: 'pointer' }}
        >
          <div style={{ width: 10, height: 20, borderRadius: '50%', background: clsConfig.color, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: '#111' }}>{tx.vendor || '—'}</span>
              {isPaired && (
                <span style={{ background: T.tealPale, color: T.tealDeep, borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>
                  <Link2 size={9} style={{ marginRight: 2 }} />linked
                </span>
              )}
              {tx.reimbursed && (
                <span title="Reimbursed" style={{ background: '#DCFCE7', color: '#15803d', borderRadius: 4, padding: '1px 5px', fontSize: 10, fontWeight: 700 }}>
                  reimbursed
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>{tx.date}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: isCredit ? '#15803d' : '#111', ...amountStyle() }}>
              {isCredit ? '+' : ''}{formatAmount(amount, currency)}
            </span>
            <ChevronDown size={14} color="#9CA3AF" style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
          </div>
        </div>

        {isExpanded && (
          <div style={{ padding: '0 14px 12px', borderTop: '1px solid #F9FAFB' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
              <ClassSelect value={cls} onChange={v => handleClassChange(tx.id, v)} />
              {showCatSelect && (
                <CategorySelect value={tx.category} onChange={v => handleCategoryChange(tx.id, v)} categories={showCatSelect} isIncome={INCOME_CLS.has(cls)} />
              )}
            </div>
            {tx.notes      && <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 6 }}>{tx.notes}</div>}
            {tx.referenceId && <div style={{ fontSize: 11, color: '#9CA3AF', marginBottom: 6 }}>Ref: {tx.referenceId}</div>}
            {isPaired && <div style={{ fontSize: 11, color: T.tealDeep, marginBottom: 8 }}>Linked to: {tx.pairedWithFile}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button onClick={e => { e.stopPropagation(); openEdit(tx); }} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid #D1D5DB', background: '#fff', color: '#374151', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                <Edit2 size={12} /> Edit
              </button>
              {isPaired ? (
                <button onClick={e => { e.stopPropagation(); handleUnlink(tx); }} disabled={isUnlinking || !uid} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid #FCD34D', background: '#FEF3C7', color: '#92400e', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <Unlink size={12} /> {isUnlinking ? 'Unlinking…' : 'Unlink'}
                </button>
              ) : (
                <button onClick={e => { e.stopPropagation(); handleDelete(tx); }} disabled={!uid} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 8, border: '1px solid #FECACA', background: '#FFF5F5', color: '#EF4444', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                  <Trash2 size={12} /> Delete
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: T.gradNavy, padding: '14px 16px 12px', flexShrink: 0 }}>
        {topBarProps && <FinanceViewTopBar {...topBarProps} />}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
          <button
            onClick={onGoToDashboard}
            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, color: '#fff', fontSize: 13, fontWeight: 600 }}
          >
            <ArrowLeft size={14} color="#fff" /> Dashboard
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontSize: 16, fontWeight: 800, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accountName}</div>
            {filename !== accountName && <div style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, marginTop: 1 }}>{filename}</div>}
            {/* Balance display */}
            {typeof balanceResult.computed_balance === 'number' && (
              <button
                onClick={() => {
                  setObAmount(accountMeta.openingBalance !== undefined ? String(accountMeta.openingBalance) : '');
                  setObDate(accountMeta.openingBalanceDate ?? '');
                  setObType(accountMeta.type ?? 'personal');
                  setShowObEdit(v => !v);
                }}
                title="Tap to view/edit opening balance"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left' }}
              >
                <span style={{
                  fontSize: 18, fontWeight: 800,
                  color: balanceResult.computed_balance < 0 ? '#fca5a5' : '#fff',
                  ...amountStyle(),
                }}>
                  {balanceResult.computed_balance < 0 ? '−' : ''}${Math.abs(balanceResult.computed_balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginLeft: 6 }}>
                  {reportType === 'credit_card' ? 'owed' : 'balance'}{!balanceResult.has_opening_balance ? ' · est.' : ''}
                </span>
              </button>
            )}
          </div>
          {saving && <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12, flexShrink: 0 }}>Saving…</span>}
          {accountFiles.some(f => !f.local) && (
            <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
              {(reportType === 'credit_card' ? onImportCreditCard : reportType === 'brokerage' ? onImportBrokerage : onImportBank) && (
                <button
                  onClick={reportType === 'credit_card' ? onImportCreditCard : reportType === 'brokerage' ? onImportBrokerage : onImportBank}
                  style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 10, padding: '8px 12px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700 }}
                >
                  <Upload size={14} color="#fff" /> Import
                </button>
              )}
              <button onClick={() => setShowAddModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, background: T.gradTeal, border: 'none', borderRadius: 10, padding: '8px 14px', cursor: 'pointer', color: '#fff', fontSize: 13, fontWeight: 700 }}>
                <Plus size={15} color="#fff" /> Add
              </button>
            </div>
          )}
        </div>
        {/* Account settings inline edit */}
        {showObEdit && (
          <div style={{ marginTop: 10, padding: '12px 14px', background: 'rgba(255,255,255,0.1)', borderRadius: 12 }}>
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 11, fontWeight: 700, marginBottom: 10 }}>ACCOUNT SETTINGS</div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
              {['personal', 'business'].map(t => (
                <button
                  key={t}
                  onClick={() => setObType(t)}
                  style={{
                    flex: 1, padding: '7px 0', borderRadius: 8, border: 'none', cursor: 'pointer',
                    fontSize: 12, fontWeight: 700, textTransform: 'capitalize',
                    background: obType === t ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.15)',
                    color: obType === t ? '#111' : 'rgba(255,255,255,0.7)',
                  }}
                >
                  {t}
                </button>
              ))}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10, fontWeight: 700, marginBottom: 6 }}>OPENING BALANCE</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number"
                placeholder="Amount"
                value={obAmount}
                onChange={e => setObAmount(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 14, fontFamily: T.fontMono, background: 'rgba(255,255,255,0.9)', color: '#111', minWidth: 0 }}
              />
              <input
                type="date"
                value={obDate}
                onChange={e => setObDate(e.target.value)}
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', fontSize: 13, background: 'rgba(255,255,255,0.9)', color: '#111', minWidth: 0 }}
              />
              <button
                onClick={handleSaveObEdit}
                disabled={obSaving}
                style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: T.tealBright, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
              >
                {obSaving ? 'Saving…' : 'Save'}
              </button>
              <button
                onClick={() => setShowObEdit(false)}
                style={{ padding: '8px 10px', borderRadius: 8, border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, cursor: 'pointer', flexShrink: 0 }}
              >
                Cancel
              </button>
            </div>
            {obError && <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 6, fontWeight: 600 }}>{obError}</div>}
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 10, marginTop: 4 }}>
              Opening balance date defaults to day before earliest transaction if left blank.
            </div>
          </div>
        )}
      </div>

      <SortFilterBar
        sortOptions={SORT_OPTIONS}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={(by, dir) => { setSortBy(by); setSortDir(dir); }}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateChange={(from, to) => { setDateFrom(from); setDateTo(to); }}
        activeFilterCount={activeFilterCount}
        onClearFilters={clearFilters}
        filterFields={<>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Vendor</label>
            <input type="text" placeholder="Search vendor…" value={filters.vendor} onChange={e => setFilter('vendor', e.target.value)} style={inputStyle} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Classification</label>
            <select value={filters.classification} onChange={e => setFilter('classification', e.target.value)} style={inputStyle}>
              <option value="">All classifications</option>
              {CLASS_SELECT_KEYS.map(k => <option key={k} value={k}>{BIZ_CLASS[k].label}</option>)}
            </select>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={labelStyle}>Category</label>
            <select value={filters.category} onChange={e => setFilter('category', e.target.value)} style={inputStyle}>
              <option value="">All categories</option>
              {Object.entries(CATEGORY_KEYS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Amount</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="number" placeholder="Min" value={filters.minAmount} onChange={e => setFilter('minAmount', e.target.value)} style={{ ...inputStyle, flex: 1 }} min="0" />
              <input type="number" placeholder="Max" value={filters.maxAmount} onChange={e => setFilter('maxAmount', e.target.value)} style={{ ...inputStyle, flex: 1 }} min="0" />
            </div>
          </div>
        </>}
        groupBy={groupBy}
        groupSections={GROUP_SECTIONS}
        onGroupChange={setGroupBy}
      />

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 36 }}>

        {/* Results count */}
        {(activeFilterCount > 0 || groupBy) && (
          <div style={{ padding: '4px 16px 8px', fontSize: 11, color: '#9ca3af' }}>
            {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
            {filteredTotals.credits > 0
              ? ` · out ${currency} ${filteredTotals.debits.toFixed(2)} · in ${currency} ${filteredTotals.credits.toFixed(2)}`
              : ` · ${currency} ${filteredTotals.debits.toFixed(2)}`
            }
          </div>
        )}

        {/* List */}
        {filtered.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#9CA3AF', padding: '48px 20px', fontSize: 15 }}>
            {activeFilterCount > 0 ? 'No matching transactions' : 'No transactions yet.'}
          </div>
        ) : grouped ? (
          <div style={{ paddingBottom: 16 }}>
            {grouped.map(group => (
              <div key={group.key}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '10px 14px 6px', borderBottom: '1.5px solid #e5e7eb', background: '#f9fafb' }}>
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#374151' }}>{group.label}</span>
                  <span style={{ fontSize: 11, color: '#9CA3AF', fontWeight: 600 }}>
                    {group.txs.length}
                    {group.credits > 0
                      ? ` · −${currency} ${group.debits.toFixed(2)} +${currency} ${group.credits.toFixed(2)}`
                      : ` · ${currency} ${group.debits.toFixed(2)}`
                    }
                  </span>
                </div>
                {group.txs.map(renderTx)}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ paddingBottom: 16 }}>
            {filtered.map(renderTx)}
          </div>
        )}

      </div>

      {showAddModal && (
        <AddTransactionModal
          transactionFiles={txFiles}
          defaultCurrency={defaultCurrency}
          categories={categories}
          incomeCategories={incomeCategories}
          onSave={handleAddTransaction}
          onClose={() => setShowAddModal(false)}
        />
      )}

      {editingTx && (
        <EditModal
          expense={editingTx}
          onSave={handleEditSave}
          onClose={() => setEditingTx(null)}
          categories={categories}
          incomeCategories={incomeCategories}
          addCategory={addCategory}
          addIncomeCategory={addIncomeCategory}
          savedFiles={savedFiles}
          currentFileName={editingTx._reportName}
          reportType={editingTx._reportType}
          lockedFields={editingTx._lockedFields ?? []}
        />
      )}
    </div>
  );
}
